/*
 *  (c) 2015 - 2023 ENISCO by FORCAM GmbH
 */

import {Dialog} from '@mui/material'
import React, {ReactNode, useMemo} from 'react'
import {useDispatch} from 'react-redux'
import useProfileOpen from '../../hooks/useProfileOpen'
import {setMyProfileOpen} from '../../store/appsSlice'
import {useTheme} from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import MyProfileDialogTitleSet from './MyProfileDialogTitleSet'
import MyProfileDialogContentSet from './MyProfileDialogContentSet'
import {TranslatedText, useUserId} from '@enisco/mcc-react-lib'
import {FormattedMessage} from 'react-intl'
import General from './tabs/general/General'
import LanguageAndTime from './tabs/language/LanguageAndTime'
import SnackBarTab from './tabs/snackbar/SnackBarTab'
import ExternalSettings from './tabs/ExternalSettings'
import useRegisteredUserSettings from '../../hooks/useRegisteredUserSettings'

export interface DialogEntry {
  key: string
  createLabel: () => string | ReactNode
  createComponent: () => ReactNode
}

const MyProfileDialog: React.FC = () => {
  const dispatch = useDispatch()
  const theme = useTheme()
  const mdDown = useMediaQuery(theme.breakpoints.down('md'))
  const [open] = useProfileOpen()
  const [value, setValue] = React.useState(0)
  const [userSettings] = useRegisteredUserSettings()
  const uid = useUserId()

  const entries: DialogEntry[] = useMemo(
    () => [
      {
        key: 'general',
        createLabel: () => (
          <FormattedMessage
            id='MyProfileDialog.General'
            defaultMessage='General'
          />
        ),
        createComponent: () => (
          <General
            sx={{
              backgroundColor: 'background.paper',
              padding: 4,
              overflow: 'auto',
              display: 'flex'
            }}
          />
        )
      },
      {
        key: 'langAndTime',
        createLabel: () => (
          <FormattedMessage
            id='MyProfileDialog.LanguageAndTime'
            defaultMessage='Language & Time'
          />
        ),
        createComponent: () => (
          <LanguageAndTime
            sx={{
              backgroundColor: 'background.paper',
              padding: 4,
              overflow: 'auto',
              display: 'flex'
            }}
          />
        )
      },
      {
        key: 'snackbar',
        createLabel: () => (
          <FormattedMessage
            id='MyProfileDialog.Snackbar'
            defaultMessage='Notifications'
          />
        ),
        createComponent: () => (
          <SnackBarTab
            sx={{
              backgroundColor: 'background.paper',
              padding: 4,
              overflow: 'auto',
              display: 'flex'
            }}
          />
        )
      },
      ...(uid === undefined
        ? []
        : userSettings.map((setting, index) => ({
            key: `crd-user-setting-${index}`,
            createLabel: () => <TranslatedText text={setting.label} />,
            createComponent: () => <ExternalSettings setting={setting} />
          })))
    ],
    [uid, userSettings]
  )

  const handleClose = () => {
    dispatch(setMyProfileOpen(false))
  }

  return (
    <Dialog
      aria-labelledby='my-profile-dialog'
      fullScreen={mdDown}
      open={open}
      PaperProps={{
        sx: {
          width: {md: '800px'},
          maxWidth: {md: '800px'},
          height: {md: '600px'},
          maxHeight: {md: '600px'}
        }
      }}
      onClose={handleClose}
    >
      <MyProfileDialogTitleSet
        onClose={handleClose}
        entries={entries}
        value={value}
        setValue={setValue}
      />
      <MyProfileDialogContentSet tabValue={value} entries={entries} />
    </Dialog>
  )
}

export default MyProfileDialog
